import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  InlineInputGroup, Check, Option, Input, Select, Textarea, Tooltip
} from 'barnyard/components';
import { BarnyardIcon } from 'barnyard/icons';
import { LEVELS } from '~/app/common/constants/levels';
import RuleForm from '../inputs/RuleForm';
import InlineItemSelect from '../inputs/InlineItemSelect';
import InlineManufacturerSelect from '../inputs/InlineManufacturerSelect';
import InlineCategorySelect from '../inputs/InlineCategorySelect';
import InlineProductLinesSelect from '../inputs/InlineProductLinesSelect';
import InlineProductSelect from '../inputs/InlineProductSelect';
import InlineRegionSelect from '../inputs/InlineRegionSelect';


const NonPreferredItemGuidance = ({
  initialValues, onSubmit, form, ...other
}) => {
  const corporateName = _.get(other, 'corporate.name', 'corporate');

  const initialState = {
    regions: !_.isEmpty(initialValues.regions) ? initialValues.regions : [0],
    itemIds: _.join(initialValues.item_ids),
    manufacturerIds: initialValues.manufacturer_ids || [],
    categoryIds: initialValues.category_ids || [],
    productIds: initialValues.product_ids || [],
    productLineIds: initialValues.product_line_ids || [],
    level: initialValues.level || LEVELS.INFO,
    search: initialValues.search || '',
    message: initialValues.message || '',
    enableAddToCartSurvey: initialValues.enable_add_to_cart_survey || false,
  };

  const [regions, setRegions] = useState(initialState.regions);
  const [itemIds, setItemIds] = useState(initialState.itemIds);
  const [manufacturerIds, setManufacturerIds] = useState(initialState.manufacturerIds);
  const [categoryIds, setCategoryIds] = useState(initialState.categoryIds);
  const [productIds, setProductIds] = useState(initialState.productIds);
  const [productLineIds, setProductLineIds] = useState(initialState.productLineIds);
  const [message, setMessage] = useState(initialState.message);
  const [level, setLevel] = useState(initialState.level);
  const [search, setSearch] = useState(initialState.search);
  const [enableAddToCartSurvey, setEnableAddToCartSurvey] = useState(initialState.enableAddToCartSurvey);

  const onFormSubmit = useCallback((event) => {
    event.preventDefault();
    onSubmit({
      kind: 'non_preferred_item',
      regions: _.filter(regions),
      value: {
        item_ids: _.filter(_.map(_.split(itemIds, ','), val => parseInt(val, 10)), val => val),
        manufacturer_ids: manufacturerIds,
        category_ids: categoryIds,
        product_ids: productIds,
        product_line_ids: productLineIds,
        message,
        level,
        search,
        enable_add_to_cart_survey: enableAddToCartSurvey,
      },
    });
  }, [
    onSubmit, regions, itemIds, manufacturerIds, categoryIds, productIds, productLineIds, level, search,
    message, enableAddToCartSurvey
  ]);

  const onReset = () => {
    setRegions(initialState.regions);
    setItemIds(initialState.itemIds);
    setManufacturerIds(initialState.manufacturerIds);
    setCategoryIds(initialState.categoryIds);
    setProductIds(initialState.productIds);
    setProductLineIds(initialState.productLineIds);
    setMessage(initialState.message);
    setLevel(initialState.level);
    setSearch(initialState.search);
    setEnableAddToCartSurvey(initialState.enableAddToCartSurvey);
  };

  return (
    <RuleForm
      {...other}
      onSubmit={onFormSubmit}
      onReset={onReset}
      isSubmitting={form.is_submitting}
    >
      <InlineRegionSelect
        onChange={setRegions}
        value={regions}
      />
      <InlineManufacturerSelect
        mode='multiple'
        label='Manufacturers'
        description='Manufacturers to be included'
        showSearch={true}
        clearable={true}
        value={manufacturerIds}
        placeholder='Manufacturers'
        onChange={setManufacturerIds}
      />
      <InlineProductLinesSelect
        mode='multiple'
        label={
          <>
            Product Lines{' '}
            <Tooltip
              direction='right'
              placement='top'
              body='Brand-level grouping (e.g., Heartgard)'
            >
              <BarnyardIcon icon={['fal', 'question-circle']} />
            </Tooltip>
          </>
        }
        description='Product Lines to be included'
        showSearch={true}
        clearable={true}
        value={productLineIds}
        placeholder='Product Lines'
        onChange={setProductLineIds}
      />
      <InlineProductSelect
        mode='multiple'
        label={
          <>
            Products{' '}
            <Tooltip
              direction='right'
              placement='top'
              body='Variation within a brand (e.g., chewables, tablets)'
            >
              <BarnyardIcon icon={['fal', 'question-circle']} />
            </Tooltip>
          </>
        }
        description='Products to be included'
        showSearch={true}
        clearable={true}
        value={productIds}
        placeholder='Products'
        onChange={setProductIds}
      />
      <InlineCategorySelect
        mode='multiple'
        label='Categories'
        description='Categories to be included'
        showSearch={true}
        clearable={true}
        value={categoryIds}
        placeholder='Categories'
        onChange={setCategoryIds}
        parentOnly={false}
      />
      <InlineItemSelect
        description='List of comma separated item ids'
        value={itemIds}
        onChange={setItemIds}
      />
      <InlineInputGroup
        label='Item Message (Optional)'
        description={(
          'You may choose to include an item message on your non-preferred items to direct clinics to your preferred '
          + 'alternative. Leave blank if you do not wish to put an item message on the non-preferred items.'
        )}
        InputComponent={Textarea}
        value={message}
        onChange={setMessage}
        placeholder='Enter message here...'
      />
      <InlineInputGroup
        label='Alert Level (Optional)'
        description='Specify the color of the message'
        InputComponent={Select}
        value={level}
        onChange={setLevel}
      >
        <Option value={LEVELS.DANGER}>Alert Message (red)</Option>
        <Option value={LEVELS.WARNING}>Warning Message (orange)</Option>
        <Option value={LEVELS.INFO}>Information Message (blue)</Option>
        <Option value={LEVELS.SUCCESS}>Positive Message (green)</Option>
      </InlineInputGroup>
      <InlineInputGroup
        label='Search (Optional)'
        description='Search to perform when the message is clicked'
        InputComponent={Input}
        value={search}
        onChange={setSearch}
        placeholder='EX: Rimadyl / Zoetis / Onsior'
      />
      <InlineInputGroup
        label='Enable Add to Cart Survey (Optional)'
        description={(
          'Check the box below to require your clinics to provide a reason before purchasing a non-preferred item. '
          + 'Please contact your account manager to configure reporting so you can receive your clinics\' responses. '
          + 'Leave blank if you would not like to require add to cart surveys.'
        )}
        InputComponent={() => (
          <Check
            label={(
              'Require clinics to complete add to cart surveys when adding non-preferred items to their cart '
              + 'on Vetcove.'
            )}
            value={enableAddToCartSurvey}
            onChange={setEnableAddToCartSurvey}
          />
        )}
      />
    </RuleForm>
  );
};

NonPreferredItemGuidance.defaultProps = {
  type: 'add',
  initialValues: {},
};

NonPreferredItemGuidance.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.object
};

export default NonPreferredItemGuidance;
